"use client"

import * as React from "react"
import {
  Puzzle,
  MinusCircle,
  Search,
  Grid,
  Check,
  X,
  Settings,
  Plus,
  FolderKanban,
  Download,
  Pencil,
  ExternalLink,
  Sparkles,
  ShieldCheck,
  Lock,
  Layers,
} from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { AppIcon } from "@/components/desk/AppIcon"
import { WidgetIcon } from "@/components/widgets/WidgetIcon"
import { App, Language, Widget, Workspace } from "@/types/workspace"
import { toast } from "@/lib/toast"
import { AppWorkspacesModal } from "./AppWorkspacesModal"
import { AddCustomAppModal } from "./AddCustomAppModal"

interface MarketplaceViewProps {
  t: Record<string, string>
  lang: Language
  marketplaceApps: App[]
  globallyInstalledApps: string[]
  developerApps: App[]
  installedApps?: any[]
  marketplaceWidgets?: Widget[]
  installedWidgets?: any[]
  widgetsConfig?: any[]
  setWidgetsConfig?: React.Dispatch<React.SetStateAction<any[]>>
  setAuthAppToShow?: (app: App) => void
  handleToggleGlobalApp: (appId: string, action?: "install" | "uninstall", workspaceIds?: string[]) => void
  handleConfigureAppWorkspaces?: (appId: string, workspaceIds: string[]) => Promise<void>
  handleToggleGlobalWidget?: (widgetId: string, action?: "install" | "uninstall") => void
  handleOpenApp?: (app: App) => void
  workspaces?: Workspace[]
  adminWorkspaces?: Workspace[]
  currentWorkspace?: Workspace | null
  currentAccount?: any
  accounts?: any[]
  user?: any
  profileRole?: string
  workspaceRole?: string
  userWorkspaceMemberships?: Array<{ workspaceId: string; role: string }>
  onAddCustomApp?: (app: App, isEdit?: boolean) => Promise<void> | void
}

export function MarketplaceView({
  t,
  lang,
  marketplaceApps,
  globallyInstalledApps,
  developerApps,
  installedApps = [],
  marketplaceWidgets = [],
  installedWidgets = [],
  widgetsConfig = [],
  setWidgetsConfig,
  setAuthAppToShow,
  handleToggleGlobalApp,
  handleConfigureAppWorkspaces,
  handleToggleGlobalWidget,
  handleOpenApp,
  workspaces = [],
  adminWorkspaces,
  currentWorkspace,
  currentAccount,
  accounts = [],
  user,
  profileRole,
  workspaceRole,
  userWorkspaceMemberships = [],
  onAddCustomApp,
}: MarketplaceViewProps) {
  const [activeTab, setActiveTab] = React.useState<"apps" | "widgets">("apps")
  const [appFilter, setAppFilter] = React.useState<"all" | "installed" | "official" | "custom">("all")
  const [searchQuery, setSearchQuery] = React.useState("")
  const [appToUninstall, setAppToUninstall] = React.useState<App | null>(null)
  const [widgetToUninstall, setWidgetToUninstall] = React.useState<any | null>(null)
  const [appForConfigModal, setAppForConfigModal] = React.useState<App | null>(null)
  const [configModalMode, setConfigModalMode] = React.useState<"install" | "configure">("install")
  const [isAddAppModalOpen, setIsAddAppModalOpen] = React.useState(false)
  const [appToEditByDeveloper, setAppToEditByDeveloper] = React.useState<App | null>(null)

  const standardIds = React.useMemo(
    () => ["system-calendar", "system-weather", "system-calculator", "system-notes"],
    []
  )

  const activeWorkspaces = React.useMemo(() => {
    return (workspaces || []).filter((w) => w.status !== false)
  }, [workspaces])

  // Workspaces ativos em que o usuário logado é Administrador
  const effectiveAdminWorkspaces = React.useMemo(() => {
    if (Array.isArray(adminWorkspaces)) {
      return adminWorkspaces.filter((w) => w.status !== false)
    }
    const isCompanyAdmin = profileRole === "owner" || profileRole === "admin"
    if (isCompanyAdmin) {
      return activeWorkspaces
    }
    return activeWorkspaces.filter((ws) => {
      if (ws.id === currentWorkspace?.id && workspaceRole === "admin") return true
      if ((ws as any).role === "admin") return true
      return userWorkspaceMemberships.some((m) => m.workspaceId === ws.id && m.role === "admin")
    })
  }, [adminWorkspaces, profileRole, activeWorkspaces, currentWorkspace?.id, workspaceRole, userWorkspaceMemberships])

  // Helper para verificar se o usuário logado é o developer_id do app
  const isUserAppDeveloper = React.useCallback(
    (app: App) => {
      if (!user?.id) return false
      const devId = String(app.developerId || (app as any).developer_id || "").toLowerCase().trim()
      const currentUserId = String(user.id).toLowerCase().trim()
      if (devId && devId === currentUserId) return true
      return developerApps.some(
        (da) =>
          (da.id === app.id || (Boolean(app.slug) && da.slug === app.slug)) &&
          String(da.developerId || (da as any).developer_id || currentUserId).toLowerCase().trim() === currentUserId
      )
    },
    [user?.id, developerApps]
  )

  const matchesAppInstallEntry = React.useCallback((ia: any, app: App) => {
    if (!ia || ia.status === "inactive") return false
    return (
      ia.app?.id === app.id ||
      (Boolean(app.slug) && ia.app?.slug === app.slug) ||
      ia.appId === app.id ||
      ia.id === app.id ||
      ia.id === `installed-${app.id}`
    )
  }, [])

  // Helper para obter os Workspaces (ativos) em que um app está instalado
  const getInstalledWorkspacesForApp = React.useCallback(
    (app: App): Workspace[] => {
      return activeWorkspaces.filter((ws) => {
        if (ws.id === currentWorkspace?.id && Array.isArray(installedApps) && installedApps.length > 0) {
          if (installedApps.some((ia) => matchesAppInstallEntry(ia, app))) return true
        }
        return ws.installedApps?.some((ia: any) => matchesAppInstallEntry(ia, app))
      })
    },
    [activeWorkspaces, currentWorkspace?.id, installedApps, matchesAppInstallEntry]
  )

  const isAppInstalledInCurrentWorkspace = React.useCallback(
    (app: App): boolean => {
      const wsInstalled = getInstalledWorkspacesForApp(app)
      if (currentWorkspace?.id) {
        if (wsInstalled.some((w) => w.id === currentWorkspace.id)) return true
        if (Array.isArray(installedApps) && installedApps.some((ia) => matchesAppInstallEntry(ia, app))) return true
        if (
          Array.isArray(currentWorkspace.installedApps) &&
          currentWorkspace.installedApps.some((ia) => matchesAppInstallEntry(ia, app))
        ) {
          return true
        }
        return false
      }
      return (
        wsInstalled.length > 0 ||
        globallyInstalledApps.includes(app.id) ||
        (Boolean(app.slug) && globallyInstalledApps.includes(app.slug))
      )
    },
    [getInstalledWorkspacesForApp, currentWorkspace, installedApps, matchesAppInstallEntry, globallyInstalledApps]
  )

  const allAvailableWidgets = React.useMemo(() => {
    const list: any[] = []
    marketplaceWidgets.forEach((mw) => {
      if (!standardIds.includes(mw.id) && !mw.isDefault && !list.some((w) => w.id === mw.id || w.slug === mw.slug)) {
        list.push(mw)
      }
    })
    widgetsConfig.forEach((cw) => {
      if (!standardIds.includes(cw.id) && !cw.isDefault && cw.type !== "system" && !list.some((w) => w.id === cw.id)) {
        list.push(cw)
      }
    })
    return list
  }, [marketplaceWidgets, widgetsConfig, standardIds])

  // Apps visíveis para a conta/usuário atual
  const accessibleApps = React.useMemo(() => {
    return marketplaceApps.filter((app) => {
      const appType = (app.type || "official").toLowerCase().trim()
      if (appType === "official" || appType === "public") {
        return true
      }

      if (appType === "private") {
        const currentAccId = currentAccount?.id ? String(currentAccount.id).toLowerCase().trim() : null
        const isAccountAllowed =
          Boolean(currentAccId) &&
          Array.isArray(app.accounts) &&
          app.accounts.some((accId: any) => String(accId).toLowerCase().trim() === currentAccId)

        const isDeveloper = isUserAppDeveloper(app)
        return Boolean(isAccountAllowed || isDeveloper)
      }

      return true
    })
  }, [marketplaceApps, currentAccount?.id, isUserAppDeveloper])

  // Contadores para os filtros de Apps
  const appCounts = React.useMemo(() => {
    let installed = 0
    let official = 0
    let custom = 0

    accessibleApps.forEach((app) => {
      const isInst = isAppInstalledInCurrentWorkspace(app)
      if (isInst) installed += 1

      const appType = String(app.type || "").toLowerCase().trim()
      if (appType === "official") {
        official += 1
      } else {
        custom += 1
      }
    })

    return {
      all: accessibleApps.length,
      installed,
      official,
      custom,
    }
  }, [accessibleApps, isAppInstalledInCurrentWorkspace])

  // Apps filtrados por categoria + busca
  const filteredApps = React.useMemo(() => {
    const query = searchQuery.toLowerCase().trim()
    let apps = accessibleApps

    if (appFilter === "installed") {
      apps = apps.filter((app) => isAppInstalledInCurrentWorkspace(app))
    } else if (appFilter === "official") {
      apps = apps.filter((app) => {
        const appType = String(app.type || "").toLowerCase().trim()
        return appType === "official"
      })
    } else if (appFilter === "custom") {
      apps = apps.filter((app) => {
        const appType = String(app.type || "").toLowerCase().trim()
        return appType === "private" || isUserAppDeveloper(app)
      })
    }

    if (!query) return apps
    return apps.filter(
      (app) =>
        app.name.toLowerCase().includes(query) ||
        (app.description && app.description.toLowerCase().includes(query)) ||
        (app.category && app.category.toLowerCase().includes(query))
    )
  }, [accessibleApps, appFilter, searchQuery, isAppInstalledInCurrentWorkspace, isUserAppDeveloper])

  const filteredWidgets = React.useMemo(() => {
    const query = searchQuery.toLowerCase().trim()
    if (!query) return allAvailableWidgets
    return allAvailableWidgets.filter(
      (widget) =>
        widget.name.toLowerCase().includes(query) ||
        (widget.description && widget.description.toLowerCase().includes(query))
    )
  }, [allAvailableWidgets, searchQuery])

  const handleWidgetToggle = (widget: any, action: "install" | "uninstall") => {
    if (widget.isDefault || standardIds.includes(widget.id)) {
      toast.error("Este widget é padrão do sistema e não pode ser alterado.")
      return
    }

    if (action === "install") {
      if (handleToggleGlobalWidget) {
        handleToggleGlobalWidget(widget.id, "install")
      } else if (setWidgetsConfig) {
        setWidgetsConfig((prev) => {
          const exists = prev.some((w) => w.id === widget.id)
          if (exists) {
            return prev.map((w) => (w.id === widget.id ? { ...w, enabled: true, visible: true } : w))
          }
          return [...prev, { ...widget, enabled: true, visible: true }]
        })
        toast.success(`Widget "${widget.name}" instalado com sucesso!`)
      }
    } else {
      if (handleToggleGlobalWidget) {
        handleToggleGlobalWidget(widget.id, "uninstall")
      } else if (setWidgetsConfig) {
        setWidgetsConfig((prev) => prev.filter((w) => w.id !== widget.id))
        toast.success(`Widget "${widget.name}" desinstalado!`)
      }
    }
  }

  const handleConfirmAppWorkspaces = async (workspaceIds: string[]) => {
    if (!appForConfigModal) return
    const appToProcess = appForConfigModal

    const adminIds = new Set(effectiveAdminWorkspaces.map((w) => w.id))
    const nonAdminInstalledIds = getInstalledWorkspacesForApp(appToProcess)
      .filter((ws) => !adminIds.has(ws.id))
      .map((ws) => ws.id)
    const mergedWorkspaceIds = Array.from(new Set([...workspaceIds, ...nonAdminInstalledIds]))

    if (handleConfigureAppWorkspaces) {
      await handleConfigureAppWorkspaces(appToProcess.id, mergedWorkspaceIds)
    } else {
      handleToggleGlobalApp(appToProcess.id, "install", mergedWorkspaceIds)
    }

    if (configModalMode === "install") {
      toast.success(`Aplicativo "${appToProcess.name}" instalado nos ambientes selecionados!`)
    } else {
      toast.success(`Configurações de ambientes do aplicativo "${appToProcess.name}" salvas com sucesso!`)
    }
  }

  return (
    <div className="flex flex-col h-full bg-zinc-50 dark:bg-zinc-950 overflow-hidden font-sans">
      {/* Header da Loja / Marketplace */}
      <div className="bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 px-6 py-4 shrink-0 space-y-3.5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="size-10 rounded-2xl bg-gradient-to-br from-[#004EB1] to-blue-600 text-white flex items-center justify-center shadow-sm shadow-[#004EB1]/20 shrink-0">
              <Puzzle className="size-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-extrabold text-zinc-900 dark:text-white tracking-tight">
                  {t.marketplaceTitle || "Marketplace da Bold"}
                </h2>
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                Explore, gerencie e instale aplicativos oficiais ou corporativos nos ambientes da sua empresa.
              </p>
            </div>
          </div>

          {/* Campo de Busca e Botão Adicionar App */}
          <div className="flex items-center gap-2.5 w-full md:w-auto">
            <div className="relative flex-1 md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400 pointer-events-none" />
              <input
                type="text"
                placeholder={activeTab === "apps" ? "Buscar aplicativos por nome ou descrição..." : "Buscar widgets..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-8 py-2 text-xs rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:outline-none focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1] transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 cursor-pointer"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </div>

            {activeTab === "apps" && (
              <Button
                type="button"
                onClick={() => {
                  setAppToEditByDeveloper(null)
                  setIsAddAppModalOpen(true)
                }}
                className="h-9 px-3.5 bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold rounded-xl flex items-center gap-1.5 shadow-sm shadow-[#004EB1]/20 cursor-pointer shrink-0 transition-all"
              >
                <Plus className="size-4" />
                <span>Adicionar App</span>
              </Button>
            )}
          </div>
        </div>

        {/* Abas Principais + Filtros Rápidos */}
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 border-t border-zinc-100 dark:border-zinc-800/80">
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setActiveTab("apps")}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${activeTab === "apps"
                ? "bg-[#004EB1] text-white shadow-xs shadow-[#004EB1]/20"
                : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800/60"
                }`}
            >
              <Grid className="size-3.5" />
              <span>Aplicativos</span>
            </button>

            <button
              onClick={() => setActiveTab("widgets")}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${activeTab === "widgets"
                ? "bg-[#004EB1] text-white shadow-xs shadow-[#004EB1]/20"
                : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800/60"
                }`}
            >
              <Puzzle className="size-3.5" />
              <span>Widgets</span>
            </button>
          </div>

          {/* Filtros de Categoria (Apenas na aba de Aplicativos) */}
          {activeTab === "apps" && (
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
              {[
                { id: "all", label: "Todos", count: appCounts.all },
                { id: "official", label: "Oficiais Bold", count: appCounts.official }
              ].map((f) => {
                const isActive = appFilter === f.id
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setAppFilter(f.id as any)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all flex items-center gap-1.5 shrink-0 cursor-pointer border ${isActive
                      ? "bg-zinc-900 text-white border-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 dark:border-zinc-100"
                      : "bg-zinc-50 dark:bg-zinc-900 text-zinc-600 dark:text-zinc-400 border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700"
                      }`}
                  >
                    <span>{f.label}</span>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Corpo com scroll */}
      <div className="flex-1 px-6 py-5 pb-24 overflow-y-auto bg-zinc-50 dark:bg-zinc-950 scrollbar-thin">
        <div className="max-w-7xl mx-auto pb-12">
          {/* ABA 1: APLICATIVOS */}
          {activeTab === "apps" && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 animate-in fade-in duration-200">
              {filteredApps.length === 0 ? (
                <div className="md:col-span-2 xl:col-span-3 py-16 text-center text-xs text-zinc-400 dark:text-zinc-500 font-sans space-y-2 bg-white dark:bg-zinc-900/50 rounded-2xl border border-dashed border-zinc-200 dark:border-zinc-800">
                  <Grid className="size-8 mx-auto opacity-30" />
                  <p className="font-medium text-zinc-600 dark:text-zinc-400">
                    {searchQuery
                      ? `Nenhum aplicativo encontrado para "${searchQuery}".`
                      : t.noMarketplaceApps || "Nenhum aplicativo encontrado nesta categoria."}
                  </p>
                </div>
              ) : (
                filteredApps.map((app) => {
                  const installedWorkspaces = getInstalledWorkspacesForApp(app)
                  const isInstalledInCurrentWorkspace = isAppInstalledInCurrentWorkspace(app)
                  const isOwnDeveloperApp = isUserAppDeveloper(app)
                  const appType = String(app.type || "").toLowerCase().trim()

                  return (
                    <Card
                      key={app.id}
                      className="group border-zinc-200/90 dark:border-zinc-800/90 bg-white dark:bg-zinc-900 rounded-2xl overflow-hidden flex flex-col justify-between hover:shadow-lg hover:border-[#004EB1]/40 dark:hover:border-blue-500/40 transition-all duration-200 p-0 gap-0"
                    >
                      {/* Parte Superior do Card: Ícone, Título, Badges e Descrição */}
                      <div className="p-4 flex flex-col gap-3 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="size-12 rounded-2xl border border-zinc-200/70 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 flex items-center justify-center shrink-0 shadow-xs group-hover:scale-[1.03] transition-transform">
                              <AppIcon
                                iconUrl={app.iconUrl}
                                iframeUrl={app.iframeUrl}
                                name={app.name}
                                size="medium"
                              />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <h3 className="text-sm font-extrabold text-zinc-900 dark:text-zinc-100 truncate">
                                  {app.name}
                                </h3>
                              </div>
                              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                {isOwnDeveloperApp ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md border bg-purple-50 border-purple-200 text-purple-700 dark:bg-purple-950/30 dark:border-purple-900/50 dark:text-purple-300">
                                    <Sparkles className="size-2.5" />
                                    {lang === "pt"
                                      ? "Meu App"
                                      : lang === "es"
                                        ? "Mi App"
                                        : "My App"}
                                  </span>
                                ) : appType === "official" ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md border bg-blue-50 border-blue-200/70 text-[#004EB1] dark:bg-blue-950/30 dark:border-blue-900/50 dark:text-blue-300">
                                    <ShieldCheck className="size-2.5" />
                                    Oficial Bold
                                  </span>
                                ) : appType === "public" ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md border bg-emerald-50 border-emerald-200/70 text-emerald-700 dark:bg-emerald-950/30 dark:border-emerald-900/50 dark:text-emerald-300">
                                    <Grid className="size-2.5" />
                                    {lang === "pt" ? "Público" : lang === "es" ? "Público" : "Public"}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md border bg-amber-50 border-amber-200/70 text-amber-700 dark:bg-amber-950/30 dark:border-amber-900/50 dark:text-amber-300">
                                    <Lock className="size-2.5" />
                                    {lang === "pt" ? "Exclusivo" : lang === "es" ? "Exclusivo" : "Exclusive"}
                                  </span>
                                )}

                                {/* Badges de Ambiente e Versão */}
                                {(() => {
                                  const env = app.environment || (app.samlAttributes as any)?.environment || "Produção"
                                  const ver = app.version || (app.samlAttributes as any)?.version || "1.0.0.0"

                                  const envBadgeClasses =
                                    env === "Alpha"
                                      ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:border-red-900/40 dark:text-red-300"
                                      : env === "Beta"
                                        ? "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900/40 dark:text-amber-300"
                                        : "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-900/40 dark:text-emerald-300"

                                  return (
                                    <>
                                      <span
                                        title={`Ambiente: ${env}`}
                                        className={`inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded border ${envBadgeClasses}`}
                                      >
                                        <span className="size-1 rounded-full bg-current shrink-0" />
                                        {env}
                                      </span>

                                      <span
                                        title={`Versão: ${ver}`}
                                        className="inline-flex items-center text-[9px] font-mono font-medium px-1.5 py-0.5 rounded border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/60 text-zinc-600 dark:text-zinc-400"
                                      >
                                        v{ver}
                                      </span>
                                    </>
                                  )
                                })()}
                              </div>
                            </div>
                          </div>

                          {/* Status Badge no canto superior direito */}
                          <span
                            className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${isInstalledInCurrentWorkspace
                              ? "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/30 dark:border-emerald-900/50 dark:text-emerald-400"
                              : "bg-zinc-100 border-zinc-200 text-zinc-500 dark:bg-zinc-800/70 dark:border-zinc-700 dark:text-zinc-400"
                              }`}
                          >
                            {isInstalledInCurrentWorkspace && <Check className="size-2.5 stroke-[3]" />}
                            {isInstalledInCurrentWorkspace
                              ? lang === "pt"
                                ? "Instalado"
                                : lang === "es"
                                  ? "Instalado"
                                  : "Installed"
                              : lang === "pt"
                                ? "Disponível"
                                : lang === "es"
                                  ? "Disponible"
                                  : "Available"}
                          </span>
                        </div>

                        {/* Descrição */}
                        <p className="text-xs text-zinc-600 dark:text-zinc-400 line-clamp-2 leading-relaxed min-h-[2.25rem]">
                          {app.description || "Sem descrição disponível para este aplicativo."}
                        </p>

                        {/* Seção de Ambientes onde o App está instalado */}
                        <div className="pt-2.5 mt-auto border-t border-zinc-100 dark:border-zinc-800/70 space-y-1.5">
                          <div className="flex items-center justify-between text-[10px] font-bold text-zinc-500 dark:text-zinc-400">
                            <span className="flex items-center gap-1.5">
                              <FolderKanban className="size-3 text-[#004EB1] dark:text-blue-400" />
                              Ambientes com este app:
                            </span>
                            <span className="font-mono text-[10px] text-zinc-400">
                              {installedWorkspaces.length}/{activeWorkspaces.length}
                            </span>
                          </div>

                          {installedWorkspaces.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {installedWorkspaces.slice(0, 4).map((ws) => {
                                const isCurrentWs = currentWorkspace?.id === ws.id
                                return (
                                  <span
                                    key={ws.id}
                                    title={isCurrentWs ? `${ws.name} (Ambiente Atual)` : ws.name}
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold truncate max-w-[140px] border ${isCurrentWs
                                      ? "bg-blue-50/90 text-[#004EB1] border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800/60"
                                      : "bg-zinc-100/90 text-zinc-700 border-zinc-200/80 dark:bg-zinc-800/80 dark:text-zinc-300 dark:border-zinc-700"
                                      }`}
                                  >
                                    {isCurrentWs && (
                                      <span className="size-1.5 rounded-full bg-[#004EB1] dark:bg-blue-400 shrink-0" />
                                    )}
                                    <span className="truncate">{ws.name}</span>
                                  </span>
                                )
                              })}
                              {installedWorkspaces.length > 4 && (
                                <span
                                  title={installedWorkspaces
                                    .slice(4)
                                    .map((w) => w.name)
                                    .join(", ")}
                                  className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700"
                                >
                                  +{installedWorkspaces.length - 4}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="text-[11px] text-zinc-400 dark:text-zinc-500 italic py-0.5">
                              Não instalado em nenhum ambiente
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Rodapé de Ações do Card */}
                      <div className="px-4 py-2.5 flex items-center justify-between gap-2 bg-zinc-50/80 dark:bg-zinc-950/50 border-t border-zinc-100 dark:border-zinc-800/80 shrink-0">
                        {/* Lado Esquerdo: Botão de Configuração do Desenvolvedor (apenas para quem é developer_id) */}
                        <div className="flex items-center gap-1.5">
                          {isOwnDeveloperApp ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setAppToEditByDeveloper(app)
                                setIsAddAppModalOpen(true)
                              }}
                              className="text-[11px] h-7 px-2.5 font-sans font-semibold border-purple-200 dark:border-purple-900/60 bg-purple-50/50 dark:bg-purple-950/20 text-purple-700 dark:text-purple-300 hover:bg-purple-100/70 dark:hover:bg-purple-950/40 transition-colors flex items-center gap-1.5 cursor-pointer"
                              title="Editar informações do aplicativo"
                            >
                              <Settings className="size-3" />
                            </Button>
                          ) : (
                            <span className="text-[10px] text-zinc-400 dark:text-zinc-500 font-medium truncate">
                              Corporativo
                            </span>
                          )}
                        </div>

                        {/* Lado Direito: Ações de Instalação / Ambientes */}
                        <div className="flex items-center gap-1.5">
                          {isInstalledInCurrentWorkspace ? (
                            <>
                              {handleOpenApp && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenApp(app)}
                                  className="text-[11px] h-7 px-2 font-sans font-semibold text-[#004EB1] dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 flex items-center gap-1 cursor-pointer"
                                  title="Abrir aplicativo no ambiente atual"
                                >
                                  <ExternalLink className="size-3" />
                                  <span>Abrir</span>
                                </Button>
                              )}

                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setAppForConfigModal(app)
                                  setConfigModalMode("configure")
                                }}
                                className="text-[11px] h-7 px-2.5 font-sans font-semibold border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors flex items-center gap-1.5 cursor-pointer"
                                title="Gerenciar em quais ambientes este app está instalado"
                              >
                                <Layers className="size-3 text-zinc-500 dark:text-zinc-400" />
                                <span>Ambientes</span>
                              </Button>

                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setAppToUninstall(app)}
                                className="text-xs h-7 w-7 p-0 font-sans font-semibold border-red-200 text-red-600 hover:bg-red-500/10 dark:border-red-900/60 dark:text-red-400 dark:hover:bg-red-950/20 transition-colors flex items-center justify-center cursor-pointer"
                                title="Desinstalar aplicativo"
                              >
                                <MinusCircle className="size-3.5" />
                              </Button>
                            </>
                          ) : (
                            <Button
                              type="button"
                              variant="default"
                              size="sm"
                              onClick={() => {
                                setAppForConfigModal(app)
                                setConfigModalMode("install")
                              }}
                              className="text-[11px] h-7 px-3 font-sans font-semibold bg-[#004EB1] hover:bg-[#003d8d] text-white flex items-center gap-1.5 cursor-pointer shadow-xs"
                            >
                              <Download className="size-3" />
                              <span>{t.installBtn || "Instalar"}</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  )
                })
              )}
            </div>
          )}

          {/* ABA 2: WIDGETS */}
          {activeTab === "widgets" && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 animate-in fade-in duration-200">
              {filteredWidgets.length === 0 ? (
                <div className="md:col-span-2 xl:col-span-3 py-16 text-center text-xs text-zinc-400 dark:text-zinc-500 font-sans space-y-2 bg-white dark:bg-zinc-900/50 rounded-2xl border border-dashed border-zinc-200 dark:border-zinc-800">
                  <Puzzle className="size-8 mx-auto opacity-30" />
                  <p className="font-medium text-zinc-600 dark:text-zinc-400">
                    {searchQuery
                      ? `Nenhum widget encontrado para "${searchQuery}".`
                      : "Nenhum widget adicional disponível no marketplace."}
                  </p>
                </div>
              ) : (
                filteredWidgets.map((widget) => {
                  const isSystem = widget.type === "system" || widget.type === "official"
                  const isInstalled =
                    widgetsConfig.some((w) => w.id === widget.id && w.enabled !== false) ||
                    installedWidgets.some((iw) => iw.widgetId === widget.id)

                  return (
                    <Card
                      key={widget.id}
                      className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 rounded-2xl overflow-hidden flex flex-col justify-between hover:shadow-md transition-all p-0 gap-0"
                    >
                      <div className="p-4 flex items-start gap-3.5 flex-1">
                        <div className="size-11 rounded-2xl border border-zinc-200/60 dark:border-zinc-800 bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center shrink-0 text-xl shadow-xs">
                          <WidgetIcon
                            id={widget.id}
                            icon={widget.icon}
                            name={widget.name}
                            className="size-6 text-[#004EB1] dark:text-blue-400"
                          />
                        </div>
                        <div className="min-w-0 space-y-1.5 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <h3 className="text-sm font-bold text-zinc-800 dark:text-zinc-100 truncate font-sans">
                              {widget.name}
                            </h3>
                            <span
                              className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${isSystem
                                ? "bg-emerald-50 border-emerald-200 text-emerald-600 dark:bg-emerald-950/20 dark:border-emerald-900/30 dark:text-emerald-400"
                                : "bg-purple-50 border-purple-200 text-purple-600 dark:bg-purple-950/20 dark:border-purple-900/30 dark:text-purple-400"
                                }`}
                            >
                              {isSystem ? "Oficial Bold" : "Customizado"}
                            </span>
                            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400">
                              {widget.dimension === "large" ? "2x1 (Grande)" : "1x1 (Pequeno)"}
                            </span>
                          </div>
                          <p className="text-xs text-zinc-600 dark:text-zinc-400 font-sans line-clamp-2 leading-relaxed">
                            {widget.description || "Sem descrição disponível."}
                          </p>
                        </div>
                      </div>

                      <div className="px-4 py-2.5 flex justify-between items-center bg-zinc-50/80 dark:bg-zinc-950/50 border-t border-zinc-100 dark:border-zinc-800/80 shrink-0">
                        <span className="text-[10px] text-zinc-400 font-sans">
                          {isInstalled ? "Instalado no ambiente" : "Disponível para instalação"}
                        </span>

                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant={isInstalled ? "outline" : "default"}
                            size="sm"
                            onClick={() => {
                              if (isInstalled) {
                                setWidgetToUninstall(widget)
                              } else {
                                handleWidgetToggle(widget, "install")
                              }
                            }}
                            className={`text-[11px] h-7 px-3 font-sans font-semibold cursor-pointer ${isInstalled
                              ? "border-red-200 text-red-600 hover:bg-red-500/10 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950/15"
                              : "bg-[#004EB1] hover:bg-[#003d8d] text-white"
                              }`}
                          >
                            {isInstalled ? "Desinstalar" : "Instalar Widget"}
                          </Button>
                        </div>
                      </div>
                    </Card>
                  )
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Confirmação de Desinstalação de Aplicativo */}
      {appToUninstall && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-4 font-sans">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-full bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 flex items-center justify-center shrink-0">
                <MinusCircle className="size-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-zinc-900 dark:text-white">Desinstalar Aplicativo</h4>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">Confirmar desinstalação</p>
              </div>
            </div>

            <p className="text-xs text-zinc-600 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-950 p-3 rounded-lg border border-zinc-150 dark:border-zinc-800 leading-relaxed">
              Tem certeza que deseja desinstalar o aplicativo{" "}
              <strong className="text-zinc-900 dark:text-white">{appToUninstall.name}</strong>? Ele continuará listado
              aqui no Marketplace e você poderá instalá-lo novamente a qualquer momento.
            </p>

            <div className="flex justify-end gap-2 pt-2 border-t border-zinc-150 dark:border-zinc-800">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setAppToUninstall(null)}
                className="text-xs h-8 cursor-pointer font-sans"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={async () => {
                  const targetApp = appToUninstall
                  setAppToUninstall(null)
                  const adminIds = new Set(effectiveAdminWorkspaces.map((w) => w.id))
                  const nonAdminInstalledIds = getInstalledWorkspacesForApp(targetApp)
                    .filter((ws) => !adminIds.has(ws.id))
                    .map((ws) => ws.id)
                  if (nonAdminInstalledIds.length > 0 && handleConfigureAppWorkspaces) {
                    await handleConfigureAppWorkspaces(targetApp.id, nonAdminInstalledIds)
                    toast.success(`Aplicativo "${targetApp.name}" desinstalado com sucesso!`)
                  } else {
                    handleToggleGlobalApp(targetApp.id, "uninstall")
                    toast.success(`Aplicativo "${targetApp.name}" desinstalado com sucesso!`)
                  }
                }}
                className="bg-red-600 hover:bg-red-700 text-white text-xs h-8 px-4 font-semibold cursor-pointer font-sans"
              >
                Confirmar Desinstalação
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Desinstalação de Widget */}
      {widgetToUninstall && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-4 font-sans">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-full bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 flex items-center justify-center shrink-0">
                <MinusCircle className="size-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-zinc-900 dark:text-white">Desinstalar Widget</h4>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">Confirmar desinstalação</p>
              </div>
            </div>

            <p className="text-xs text-zinc-600 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-950 p-3 rounded-lg border border-zinc-150 dark:border-zinc-800 leading-relaxed">
              Tem certeza que deseja desinstalar o widget{" "}
              <strong className="text-zinc-900 dark:text-white">{widgetToUninstall.name}</strong> do seu ambiente?
            </p>

            <div className="flex justify-end gap-2 pt-2 border-t border-zinc-150 dark:border-zinc-800">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setWidgetToUninstall(null)}
                className="text-xs h-8 cursor-pointer font-sans"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  handleWidgetToggle(widgetToUninstall, "uninstall")
                  setWidgetToUninstall(null)
                }}
                className="bg-red-600 hover:bg-red-700 text-white text-xs h-8 px-4 font-semibold cursor-pointer font-sans"
              >
                Confirmar Desinstalação
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Escolha / Configuração de Workspaces do Aplicativo */}
      <AppWorkspacesModal
        isOpen={Boolean(appForConfigModal)}
        onClose={() => setAppForConfigModal(null)}
        app={appForConfigModal}
        mode={configModalMode}
        workspaces={effectiveAdminWorkspaces}
        currentWorkspace={currentWorkspace}
        onConfirm={handleConfirmAppWorkspaces}
        t={t}
        lang={lang}
      />

      {/* Modal de Adicionar / Configurar Aplicativo Personalizado (Desenvolvedor) */}
      <AddCustomAppModal
        isOpen={isAddAppModalOpen}
        onClose={() => {
          setIsAddAppModalOpen(false)
          setAppToEditByDeveloper(null)
        }}
        appToEdit={appToEditByDeveloper}
        currentAccount={currentAccount}
        accounts={accounts}
        user={user}
        onSave={async (savedApp, isEdit) => {
          if (onAddCustomApp) {
            await onAddCustomApp(savedApp, isEdit)
          }
        }}
        t={t}
        lang={lang}
      />
    </div>
  )
}
